// utils/purchaseCreateIdempotency.ts
//
// Idempotency-key lifecycle + canonical payload/fingerprint helpers for
// POST /purchases, used by pages/PurchaseEntry.tsx -- the only frontend
// caller of this endpoint.
//
// SHAPE: mirrors utils/saleIdempotencyKey.ts (a single-resource "create new
// document" draft flow: one active key per in-progress form, no resource id
// to scope by yet), NOT the Deposit write-off / Vendor Payment / Customer
// Payment per-resource Map pattern -- PurchaseEntry.tsx, like SaleEntry.tsx,
// is a "new Purchase" form with exactly one draft in flight at a time, so
// there is nothing to key a Map by.
//
// FAILURE CLASSIFICATION: unlike saleIdempotencyKey.ts (whose page,
// SaleEntry.tsx, inlines a weaker ad hoc check that lumps an idempotency-
// conflict 409 in with an ambiguous outcome and never resets the key after
// one), this module borrows utils/vendorPaymentIdempotency.ts's proper
// four-way classifier (definite-rejection / closed-period /
// idempotency-conflict / ambiguous) and matching resolve* transitions.
// backend/purchases_router.create_purchase raises the exact same shape of
// errors Vendor Payment does -- a closed-period 409 (assert_year_open,
// called first inside _create_operation, before any row is touched) and an
// idempotency-conflict 409 (IdempotencyRequestConflict, mapped in the route)
// are the only two 409 sources; every other 4xx (400 missing/invalid
// idempotency_key, vendor/product/account not found, non-positive/
// over-total amount_paid, payment account required; 401/403 from
// get_current_user; 422 from request validation) occurs strictly before any
// business write. Both 409 varieties, like every other 4xx, are therefore
// DEFINITE outcomes (nothing was persisted under the key), so the resolver
// for closed-period and for idempotency-conflict is the same as for a plain
// definite rejection -- reset to idle -- but they stay distinct classifier
// values because they need distinct user-facing messages.

export type PurchaseLinePayload = {
  product_id: number;
  qty: string;
  cost_price: string;
  discount: string;
};

export type PurchaseCreatePayload = {
  vendor_id: number;
  date: string;
  due_date: string | null;
  invoice_number: string | null;
  memo: string | null;
  source_document_id: number | null;
  lines: PurchaseLinePayload[];
  amount_paid: string;
  payment_account_id: number | null;
};

/**
 * Deterministic canonical payload for POST /purchases -- mirrors
 * backend/purchases_router._purchase_create_canonical_payload's field list
 * and reasoning exactly (see that function's docstring for why `amount`,
 * `status`, and per-line `total` are excluded as non-material, and why line
 * order is preserved rather than sorted). Callers must pass values derived
 * from the SAME data that becomes the actual request body, never a
 * separately reconstructed copy, so the fingerprint can never drift from
 * what is truly sent.
 *
 * qty/cost_price/discount are normalized to fixed 2-decimal strings so
 * "10", "10.0", "10.00" all canonicalize identically -- matching the
 * backend's own q2() canonicalization of these same fields.
 */
export function canonicalPurchaseCreate(
  vendorId: string | number,
  date: string,
  dueDate: string | null | undefined,
  invoiceNumber: string | null | undefined,
  memo: string | null | undefined,
  sourceDocumentId: number | null | undefined,
  lines: Array<{
    product_id: string | number;
    qty: string | number;
    cost_price: string | number;
    discount?: string | number | null;
  }>,
  amountPaid: string | number | null | undefined,
  paymentAccountId: string | number | null | undefined,
): PurchaseCreatePayload {
  const toFixed2 = (value: string | number | null | undefined): string => {
    const n = typeof value === "number" ? value : parseFloat(String(value ?? "").trim());
    return (Number.isFinite(n) ? n : 0).toFixed(2);
  };

  return {
    vendor_id: Number(vendorId),
    date,
    due_date: dueDate || null,
    invoice_number: invoiceNumber ? invoiceNumber.trim() || null : null,
    memo: memo ? memo.trim() || null : null,
    source_document_id:
      sourceDocumentId !== null && sourceDocumentId !== undefined
        ? Number(sourceDocumentId)
        : null,
    lines: lines.map((line) => ({
      product_id: Number(line.product_id),
      qty: toFixed2(line.qty),
      cost_price: toFixed2(line.cost_price),
      discount: toFixed2(line.discount ?? 0),
    })),
    amount_paid: toFixed2(amountPaid ?? 0),
    payment_account_id:
      paymentAccountId !== null && paymentAccountId !== undefined && paymentAccountId !== ""
        ? Number(paymentAccountId)
        : null,
  };
}

export function fingerprintPurchaseCreate(payload: PurchaseCreatePayload): string {
  return JSON.stringify(payload);
}

export function randomUuidKey(): string {
  return crypto.randomUUID();
}

export type IdempotencyKeyRef = { current: string | null };

/**
 * Returns ref.current if already set, otherwise generates a new key via
 * generateKey(), stores it on the ref, and returns it. Mirrors
 * saleIdempotencyKey.getOrCreateIdempotencyKey exactly -- callers own the
 * ref's lifetime; setting ref.current back to null is what allows the next
 * call to mint a new key.
 */
export function getOrCreatePurchaseIdempotencyKey(
  ref: IdempotencyKeyRef,
  generateKey: () => string,
): string {
  if (!ref.current) {
    ref.current = generateKey();
  }
  return ref.current;
}

export type PurchaseSubmissionIdentityDecision =
  | "first-attempt"
  | "unchanged-retry"
  | "mismatch";

/**
 * Pure branching logic for whether a submit attempt is the first real
 * attempt for this draft, a retry of the exact same payload, or a retry of
 * an EDITED payload (which must be blocked). Mirrors
 * saleIdempotencyKey.decideSaleSubmissionIdentity exactly.
 */
export function decidePurchaseSubmissionIdentity(
  storedFingerprint: string | null,
  currentFingerprint: string,
): PurchaseSubmissionIdentityDecision {
  if (storedFingerprint === null) return "first-attempt";
  if (storedFingerprint === currentFingerprint) return "unchanged-retry";
  return "mismatch";
}

// ---------------------------------------------------------------------------
// Attempt lifecycle (idle / in-flight / ambiguous)
// ---------------------------------------------------------------------------

export type PurchaseCreateAttemptState = "idle" | "in-flight" | "ambiguous";

export type PurchaseCreateAttempt = {
  state: PurchaseCreateAttemptState;
  idempotencyKey: string | null;
  submittedFingerprint: string | null;
};

export const IDLE_PURCHASE_CREATE_ATTEMPT: PurchaseCreateAttempt = {
  state: "idle",
  idempotencyKey: null,
  submittedFingerprint: null,
};

/** Confirmed 2xx success. Clears the attempt back to idle -- caller must
 * also clear the persisted draft's idempotency fields and the in-memory
 * ref. */
export function resolvePurchaseCreateSuccess(): PurchaseCreateAttempt {
  return IDLE_PURCHASE_CREATE_ATTEMPT;
}

/**
 * Definite pre-commit rejection -- ANY deterministic 4xx this endpoint's
 * stack can produce, INCLUDING a closed-period 409 and an
 * idempotency-conflict 409 (see module docstring for why both are definite,
 * not ambiguous, for this endpoint). Nothing was persisted under this key
 * in any of these cases, so the identity is free to reuse. Returns to idle
 * so the next submission mints a fresh key and the user is free to edit any
 * field.
 */
export function resolvePurchaseCreateDefiniteRejection(): PurchaseCreateAttempt {
  return IDLE_PURCHASE_CREATE_ATTEMPT;
}

/**
 * Ambiguous outcome: network failure, timeout, a lost/malformed/unreadable
 * response, HTTP 500, or any other unexpected server error where the
 * operation may have committed before the response was constructed (or
 * before _load_purchase_for_replay's post-commit validation ran). Retains
 * the key and fingerprint so an unchanged retry safely replays instead of
 * risking a duplicate; an edited retry stays blocked.
 */
export function resolvePurchaseCreateAmbiguousFailure(
  attempt: PurchaseCreateAttempt,
): PurchaseCreateAttempt {
  return { ...attempt, state: "ambiguous" };
}

// ---------------------------------------------------------------------------
// Failure classification
// ---------------------------------------------------------------------------

export type PurchaseCreateFailureClass =
  | "definite-rejection"
  | "closed-period"
  | "idempotency-conflict"
  | "ambiguous";

/**
 * Classifies a thrown apiPost error into one of the four outcomes this
 * endpoint can produce. Mirrors utils/vendorPaymentIdempotency's
 * classifyVendorPaymentFailure exactly, including its 409-status
 * disambiguation (isClosedPeriod's message match distinguishes the
 * closed-period 409 from the idempotency-conflict 409 -- confirmed by
 * tracing every HTTPException create_purchase's _create_operation and its
 * helpers can raise: there is no third 409 variant).
 *
 * `status` (added to apiPost's thrown error by api/client.ts) distinguishes
 * a genuine HTTP response (4xx/5xx) from a network-level failure: fetch()
 * itself rejecting throws a plain Error/TypeError with no `.response` at
 * all, so `err?.response?.status` is `undefined` for those -- correctly
 * folded into "ambiguous", the same as a malformed/unreadable response.
 * 5xx: ambiguous -- _load_purchase_for_replay runs AFTER db.commit(), so a
 * 500 can mean the Purchase WAS persisted and only response construction or
 * replay validation failed.
 */
export function classifyPurchaseCreateFailure(
  err: any,
  isClosedPeriod: (err: any) => boolean,
): PurchaseCreateFailureClass {
  const status = err?.response?.status;

  if (typeof status !== "number") {
    return "ambiguous";
  }

  if (status === 409) {
    return isClosedPeriod(err) ? "closed-period" : "idempotency-conflict";
  }

  if (status >= 400 && status < 500) {
    return "definite-rejection";
  }

  return "ambiguous";
}

// ---------------------------------------------------------------------------
// User-facing messages
// ---------------------------------------------------------------------------

export const PURCHASE_CREATE_AMBIGUOUS_RETRY_BLOCKED_MESSAGE =
  "The result of your last attempt to save this purchase is uncertain. " +
  "Retry with the exact same details to safely resolve it, or check the " +
  "Purchases list before changing any details.";

/**
 * Shown for an idempotency-conflict 409: the retained key is permanently
 * bound to a different, already-recorded purchase. Deliberately does NOT
 * suggest the purchase may ambiguously have been recorded under THESE
 * details -- it was not; only a differently-shaped request was ever
 * persisted under this key.
 */
export const PURCHASE_CREATE_CONFLICT_MESSAGE =
  "This purchase could not be saved because its idempotency key was " +
  "already used for a different request. Please try saving it again.";
